import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, ArrowRight, CheckCircle2 } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "trade", label: "Trade", required: true },
  { key: "email", label: "Email", required: false },
  { key: "cert_type", label: "Certification Type", required: false },
  { key: "expiration_date", label: "Expiration Date", required: false },
];

function autoDetect(col) {
  const c = col.toLowerCase();
  if (/(first[\s_]*name|full[\s_]*name|worker|employee|^name$)/.test(c)) return "name";
  if (/(trade|role|job[\s_]*title|position)/.test(c)) return "trade";
  if (/email/.test(c)) return "email";
  if (/cert|certification|type/.test(c)) return "cert_type";
  if (/exp|expir/.test(c)) return "expiration_date";
  return "";
}

export default function Import() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultCert, setDefaultCert] = useState("");
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [certTypes, setCertTypes] = useState([]);

  useEffect(() => { api.get("/meta/cert-types").then(r => setCertTypes(r.data.types || [])); }, []);

  const handleUpload = async (f) => {
    setFile(f);
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      // auto-map
      const auto = {};
      FIELDS.forEach((fld) => {
        const match = (data.columns || []).find((c) => autoDetect(c) === fld.key);
        if (match) auto[fld.key] = match;
      });
      setMapping(auto);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const commit = async () => {
    if (!mapping.name || !mapping.trade) {
      toast.error("Map at least Name and Trade");
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/import/commit", {
        upload_id: preview.upload_id,
        mapping,
        default_cert_type: defaultCert || null,
      });
      setResult(data);
      toast.success(`${data.created_employees} employees created`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCommitting(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setMapping({}); setResult(null); setDefaultCert("");
  };

  return (
    <div className="p-4 md:p-10 max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">Bulk Onboarding</div>
        <h1 className="font-heading text-4xl md:text-6xl">SPREADSHEET IMPORT</h1>
        <p className="text-muted-foreground mt-2 text-sm md:text-base">
          Upload <b>.xlsx</b> or <b>.csv</b>, map columns, and we&apos;ll create employees + certifications in one shot.
        </p>
      </div>

      {!preview && !result && (
        <label className="brutal-card block p-10 text-center cursor-pointer hover:bg-safety-yellow/10 transition-colors">
          <input data-testid="import-file" type="file" accept=".xlsx,.csv" className="hidden"
            onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
          <UploadCloud className="w-16 h-16 mx-auto text-safety-yellow" strokeWidth={1.5} />
          <div className="font-heading text-3xl mt-4">Drop a file or click to upload</div>
          <div className="text-sm text-muted-foreground mt-2 font-bold uppercase tracking-wider">.XLSX · .CSV</div>
          {uploading && <div className="mt-4 font-bold uppercase tracking-wider">Processing...</div>}
        </label>
      )}

      {preview && !result && (
        <div className="space-y-6">
          <div className="brutal-card p-5 md:p-7">
            <div className="flex items-center gap-3 mb-4">
              <FileSpreadsheet className="w-6 h-6 text-safety-yellow" />
              <div className="font-heading text-2xl">{file?.name}</div>
              <span className="chip bg-muted text-foreground">{preview.total_rows} rows</span>
            </div>

            <div className="text-xs font-bold uppercase tracking-wider mb-3 text-muted-foreground">Map your columns</div>
            <div className="grid md:grid-cols-2 gap-4">
              {FIELDS.map((fld) => (
                <div key={fld.key}>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">
                    {fld.label} {fld.required && <span className="text-status-expired">*</span>}
                  </label>
                  <select data-testid={`map-${fld.key}`} className="industrial-input"
                    value={mapping[fld.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [fld.key]: e.target.value })}>
                    <option value="">— Not mapped —</option>
                    {preview.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {!mapping.cert_type && (
              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">
                  Default Certification Type (applied when no column mapped)
                </label>
                <select data-testid="default-cert" className="industrial-input"
                  value={defaultCert} onChange={(e) => setDefaultCert(e.target.value)}>
                  <option value="">— None —</option>
                  {certTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}

            <div className="mt-6 overflow-x-auto">
              <div className="text-xs font-bold uppercase tracking-wider mb-2 text-muted-foreground">Preview (first 20)</div>
              <table className="w-full border-2 border-foreground dark:border-safety-yellow text-xs">
                <thead>
                  <tr className="bg-safety-yellow text-black">
                    {preview.columns.map((c) => (
                      <th key={c} className="border-2 border-black p-2 text-left uppercase tracking-wider">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={`preview-row-${i}`} className="border-t-2 border-foreground dark:border-safety-yellow">
                      {preview.columns.map((c) => (
                        <td key={c} className="p-2 border-r-2 border-foreground/30 dark:border-safety-yellow/40">{row[c]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 flex-wrap">
            <button data-testid="import-cancel" onClick={reset} className="btn-ghost">Cancel</button>
            <button data-testid="import-commit" onClick={commit} disabled={committing} className="btn-primary">
              {committing ? "Importing..." : (<>Commit Import <ArrowRight className="w-5 h-5 ml-2" strokeWidth={3} /></>)}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="brutal-card p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-status-current mx-auto mb-4" strokeWidth={2.5} />
          <div className="font-heading text-4xl mb-2">IMPORT COMPLETE</div>
          <div className="text-sm text-muted-foreground mb-6">
            <b className="text-foreground">{result.created_employees}</b> new employees ·
            {" "}<b className="text-foreground">{result.created_certifications}</b> certifications
          </div>
          {result.errors?.length > 0 && (
            <div className="text-left border-2 border-status-expired p-3 mb-6">
              <div className="text-xs font-bold uppercase tracking-wider text-status-expired mb-2">Warnings</div>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((er) => <li key={`err-${er.row}-${er.error}`}>Row {er.row}: {er.error}</li>)}
              </ul>
            </div>
          )}
          <button onClick={reset} data-testid="import-again" className="btn-primary w-full">Import Another</button>
        </div>
      )}
    </div>
  );
}
